import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import FormateurPageShell from '../formateur/FormateurPageShell';
import AdminToast from '../admin/AdminToast';
import UserProfileAvatar from '../common/UserProfileAvatar';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { authService } from '../../services/authService';
import { uploadProfilePhoto } from '../../services/academiaFormateurService';
import { fetchAmbassadeurProfil, updateAmbassadeurProfil } from '../../services/academiaAmbassadeurService';

type AmbassadeurUser = {
  telephone?: string;
  email?: string;
  mustCompleteProfile?: boolean;
  mustChangePassword?: boolean;
  profil?: { prenom?: string; nom?: string; photoUrl?: string | null };
};

type ProfilData = {
  telephone?: string;
  email?: string;
  codeParrainage?: string | null;
  profil?: { prenom?: string; nom?: string; urlAvatar?: string | null };
  profilAmbassadeur?: {
    zonePromotion?: string | null;
    biographie?: string | null;
    profilComplet?: boolean;
  };
};

type Props = {
  user?: AmbassadeurUser;
  onboarding?: boolean;
  onProfileSaved?: () => void | Promise<void>;
};

export default function AmbassadorProfilPanel({ user, onboarding = false, onProfileSaved }: Props) {
  const [profilData, setProfilData] = useState<ProfilData | null>(null);
  const [biographie, setBiographie] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [photoUrl, setPhotoUrl] = useState(user?.profil?.photoUrl ?? '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = (await fetchAmbassadeurProfil()) as ProfilData;
      setProfilData(data);
      setBiographie(String(data?.profilAmbassadeur?.biographie ?? ''));
      const avatar = data?.profil?.urlAvatar ?? user?.profil?.photoUrl ?? '';
      setPhotoUrl(avatar || '');
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Impossible de charger le profil');
    } finally {
      setLoading(false);
    }
  }, [user?.profil?.photoUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [photoFile]);

  const prenom = profilData?.profil?.prenom ?? user?.profil?.prenom ?? '';
  const nom = profilData?.profil?.nom ?? user?.profil?.nom ?? '';
  const displayName = [prenom, nom].filter(Boolean).join(' ') || user?.telephone || 'Ambassadeur';
  const telephone = profilData?.telephone ?? user?.telephone;
  const email = profilData?.email ?? user?.email;
  const zonePromotion = profilData?.profilAmbassadeur?.zonePromotion?.trim() || null;
  const codeParrainage = profilData?.codeParrainage?.trim() || null;
  const mustChangePassword = Boolean(user?.mustChangePassword);
  const showOnboarding = onboarding || Boolean(user?.mustCompleteProfile);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!biographie.trim()) {
      setToast({ message: 'La présentation est requise.', variant: 'error' });
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      let nextPhotoUrl: string | undefined;
      if (photoFile) {
        setIsUploadingPhoto(true);
        nextPhotoUrl = await uploadProfilePhoto(photoFile);
        setPhotoUrl(nextPhotoUrl);
        setIsUploadingPhoto(false);
      }
      await updateAmbassadeurProfil({
        biographie: biographie.trim(),
        ...(nextPhotoUrl ? { photoUrl: nextPhotoUrl } : {}),
      });
      if (nextPhotoUrl) {
        const stored = authService.getStoredAuth().user;
        if (stored?.profil) {
          stored.profil.photoUrl = nextPhotoUrl;
          localStorage.setItem('onego_user', JSON.stringify(stored));
        }
      }
      setPhotoFile(null);
      setToast({
        message: showOnboarding
          ? 'Profil enregistré. Définissez votre mot de passe personnel dans la section « Sécurité du compte ».'
          : 'Profil enregistré avec succès.',
        variant: 'success',
      });
      await onProfileSaved?.();
      await load();
    } catch (err) {
      setToast({ message: err instanceof Error ? err.message : 'Enregistrement impossible', variant: 'error' });
    } finally {
      setSaving(false);
      setIsUploadingPhoto(false);
    }
  }

  return (
    <>
      <AdminToast message={toast?.message ?? null} variant={toast?.variant ?? 'success'} onClose={() => setToast(null)} />
      <FormateurPageShell
        title="Mon profil ambassadeur"
        description={
          showOnboarding
            ? 'Complétez votre présentation pour activer le partage de votre code, puis définissez votre mot de passe personnel.'
            : 'Votre identité, votre canal de promotion assigné et votre présentation publique.'
        }
        isLoading={loading && !profilData && !loadError}
        isError={Boolean(loadError)}
        errorMessage={loadError}
        onRetry={() => void load()}
        sideCard={{
          label: 'Code parrainage',
          value: codeParrainage || '—',
          helper: zonePromotion || displayName,
          sideLinks: [
            { label: 'Parrainage', to: '/ambassadeur/parrainage', icon: 'share' },
            { label: 'Portefeuille', to: '/ambassadeur/wallet', icon: 'account_balance_wallet' },
          ],
        }}
        primaryAction={
          showOnboarding ? (
            <span className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-md">
              <LucideSymbolIcon name="info" tone="inherit" className="text-white" size={14} />
              Étape 1 sur 2 — profil
            </span>
          ) : undefined
        }
      >
        <div className="formateur-profil">
          {showOnboarding ? (
            <div className="formateur-profil-onboarding" role="status">
              <LucideSymbolIcon name="info" tone="inherit" size={20} className="shrink-0 text-amber-800" />
              <div>
                <p className="font-bold">Bienvenue sur JCONSULT MY Academia</p>
                <p className="mt-1">
                  Ajoutez votre photo et votre présentation, enregistrez, puis choisissez votre mot de passe personnel
                  dans la section « Sécurité du compte ».
                </p>
              </div>
            </div>
          ) : null}

          <form className="formateur-profil__grid" onSubmit={handleSubmit}>
            <section className="formateur-profil-panel" aria-labelledby="ambassadeur-profil-identity-heading">
              <header className="formateur-profil-panel__header">
                <span className="formateur-profil-panel__header-icon" aria-hidden>
                  <LucideSymbolIcon name="person" tone="inherit" size={22} />
                </span>
                <div className="min-w-0">
                  <h2 id="ambassadeur-profil-identity-heading" className="formateur-profil-panel__title">
                    Identité
                  </h2>
                  <p className="formateur-profil-panel__subtitle">Photo et coordonnées de contact</p>
                </div>
              </header>
              <div className="formateur-profil-panel__body">
                <div className="formateur-profil-identity__hero">
                  <UserProfileAvatar
                    name={displayName}
                    prenom={prenom}
                    nom={nom}
                    photoUrl={photoUrl || null}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="formateur-profil-identity__name">{displayName}</p>
                    <label className="formateur-profil__btn formateur-profil__btn--outline mt-2">
                      <LucideSymbolIcon name="upload" tone="inherit" size={16} />
                      {isUploadingPhoto ? 'Téléversement…' : 'Changer la photo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                      />
                    </label>
                    {photoFile ? (
                      <p className="formateur-profil-identity__hint">Nouvelle photo : {photoFile.name}</p>
                    ) : null}
                    {photoPreviewUrl ? (
                      <div className="formateur-profil-identity__preview">
                        <p className="formateur-profil-identity__hint">Prévisualisation</p>
                        <img src={photoPreviewUrl} alt="" className="formateur-profil-identity__preview-img" />
                      </div>
                    ) : null}
                  </div>
                </div>

                <ul className="formateur-profil-facts">
                  <li className="formateur-profil-fact">
                    <span className="formateur-profil-fact__icon" aria-hidden>
                      <LucideSymbolIcon name="badge" tone="inherit" size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="formateur-profil-fact__label">Nom affiché</p>
                      <p className="formateur-profil-fact__value">{displayName}</p>
                    </div>
                  </li>
                  <li className="formateur-profil-fact">
                    <span className="formateur-profil-fact__icon" aria-hidden>
                      <LucideSymbolIcon name="call" tone="inherit" size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="formateur-profil-fact__label">Téléphone</p>
                      <p className="formateur-profil-fact__value">{telephone?.trim() || 'Non renseigné'}</p>
                    </div>
                  </li>
                  <li className="formateur-profil-fact">
                    <span className="formateur-profil-fact__icon" aria-hidden>
                      <LucideSymbolIcon name="mail" tone="inherit" size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="formateur-profil-fact__label">E-mail</p>
                      <p className="formateur-profil-fact__value break-all">{email?.trim() || 'Non renseigné'}</p>
                    </div>
                  </li>
                  <li className="formateur-profil-fact">
                    <span className="formateur-profil-fact__icon" aria-hidden>
                      <LucideSymbolIcon name="campaign" tone="inherit" size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="formateur-profil-fact__label">Zone / canal de promotion</p>
                      <p className="formateur-profil-fact__value">
                        {zonePromotion || 'Non assignée — contactez l’administration'}
                      </p>
                    </div>
                  </li>
                  {codeParrainage ? (
                    <li className="formateur-profil-fact">
                      <span className="formateur-profil-fact__icon" aria-hidden>
                        <LucideSymbolIcon name="qr_code_2" tone="inherit" size={18} />
                      </span>
                      <div className="min-w-0">
                        <p className="formateur-profil-fact__label">Code parrainage</p>
                        <p className="formateur-profil-fact__value font-mono">{codeParrainage}</p>
                      </div>
                    </li>
                  ) : null}
                </ul>
              </div>
            </section>

            <section
              className="formateur-profil-panel formateur-profil-panel--wide"
              aria-labelledby="ambassadeur-profil-presentation-heading"
            >
              <header className="formateur-profil-panel__header">
                <span className="formateur-profil-panel__header-icon" aria-hidden>
                  <LucideSymbolIcon name="edit_note" tone="inherit" size={22} />
                </span>
                <div className="min-w-0">
                  <h2 id="ambassadeur-profil-presentation-heading" className="formateur-profil-panel__title">
                    Présentation
                  </h2>
                  <p className="formateur-profil-panel__subtitle">
                    Décrivez comment vous faites connaître la plateforme
                  </p>
                </div>
              </header>
              <div className="formateur-profil-panel__body">
                <div className="formateur-profil-form">
                  <label className="formateur-profil-form__field formateur-profil-form__field--wide">
                    <span className="formateur-profil-form__label">Biographie *</span>
                    <textarea
                      className="formateur-profil-form__input formateur-profil-form__textarea"
                      rows={6}
                      placeholder="Présentez-vous et expliquez comment vous accompagnez les futurs apprenants…"
                      value={biographie}
                      onChange={(e) => setBiographie(e.target.value)}
                      required
                    />
                  </label>
                </div>
                <div className="formateur-profil-panel__actions">
                  <button
                    type="submit"
                    disabled={saving || isUploadingPhoto}
                    className="formateur-profil__btn formateur-profil__btn--primary"
                  >
                    <LucideSymbolIcon name="save" tone="inherit" size={16} className="text-white" />
                    {saving ? 'Enregistrement…' : 'Enregistrer le profil'}
                  </button>
                </div>
              </div>
            </section>
          </form>

          <section className="formateur-profil-panel" aria-labelledby="ambassadeur-profil-security-heading">
            <header className="formateur-profil-panel__header">
              <span className="formateur-profil-panel__header-icon formateur-profil-panel__header-icon--security" aria-hidden>
                <LucideSymbolIcon name="lock" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="ambassadeur-profil-security-heading" className="formateur-profil-panel__title">
                  Sécurité du compte
                </h2>
                <p className="formateur-profil-panel__subtitle">
                  {mustChangePassword
                    ? 'Remplacez le code provisoire par un mot de passe personnel (8 caractères minimum).'
                    : 'Modifiez votre mot de passe de connexion à tout moment.'}
                </p>
              </div>
            </header>
            <div className="formateur-profil-panel__body">
              <div className="formateur-profil-panel__actions">
                <Link
                  to="/ambassadeur/changer-mot-de-passe"
                  className="formateur-profil__btn formateur-profil__btn--primary"
                >
                  <LucideSymbolIcon name="lock" tone="inherit" size={16} className="text-white" />
                  {mustChangePassword ? 'Définir mon mot de passe' : 'Changer le mot de passe'}
                </Link>
              </div>
            </div>
          </section>
        </div>
      </FormateurPageShell>
    </>
  );
}
